import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, Loader2, Play, RotateCw, Store, X } from "lucide-react";
import type { JobRow, JobSummary } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { useEventListener } from "../lib/events.tsx";
import { jobTitle } from "../lib/labels.ts";
import { useToast } from "../lib/toast.tsx";
import {
  PIPELINE_JOBS,
  bumpDrifted,
  bumpHistory,
  currentVersions,
  fmtVersion,
  headline,
  latestSucceeded,
  pendingBump,
  recommendNext,
  shortTime,
  stepSummary,
  type BuildResult,
} from "../lib/release.ts";
import { AppIcon } from "../components/AppIcon.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import {
  Banner,
  Button,
  Card,
  Dot,
  Empty,
  ErrorState,
  RowLink,
  StatusPill,
  statusLabel,
  toneColor,
  type Tone,
} from "../components/Ui.tsx";
import { JobLauncher, type LaunchContext } from "../components/JobLauncher.tsx";
import { RunPanel } from "../components/RunPanel.tsx";

/**
 * Uygulama sayfası: yayın hattı önde, sürüm kaynakları ve uyarılar hemen altında.
 *
 * Hattın sırası tasarımdan geliyor ve tesadüfi değil — sürüm kontrolü geçmeden
 * derleme başlatmak, Play'de kullanılmış bir versionCode'la build almak demek.
 *
 * Buradan başlatılan iş sayfadan ayrılmadan izlenir (RunPanel); bitince hattın
 * sonraki adımı tek tıkla açılır.
 */
const STEP_TITLES: Record<(typeof PIPELINE_JOBS)[number], string> = {
  "release.version.check": "Sürüm kontrolü",
  "release.plan": "Değişiklik analizi",
  "release.version.bump": "Sürüm yükselt",
  "build.gradle": "Derleme",
  "play.upload": "Play'e yükle",
};
const PIPELINE = PIPELINE_JOBS.map((job, i) => ({ n: i + 1, job, title: STEP_TITLES[job] }));
const PIPELINE_SET = new Set<string>(PIPELINE_JOBS);

/** Parametresiz, onaysız işler form açmadan başlar (yalnızca uygulama kimliği ister). */
const QUICK = new Set(["release.version.check", "release.plan"]);

const ACTIVE = new Set(["queued", "claimed", "running"]);

const VERSION_LABELS: Record<string, string> = {
  appJsonVersion: "app.json version",
  appJsonVersionCode: "app.json versionCode",
  gradleVersionName: "build.gradle versionName",
  gradleVersionCode: "build.gradle versionCode",
  runtimeVersion: "runtimeVersion",
  runtimePolicy: "runtime politikası",
};

interface Launching {
  summary: JobSummary;
  initialParams?: unknown;
  fromPipeline?: boolean;
}

export function AppDetailPage(): ReactNode {
  const { appId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [launching, setLaunching] = useState<Launching | null>(null);
  const [running, setRunning] = useState<{ id: string; type: string } | null>(null);
  // Hangi hızlı iş başlatılıyor: çift tıklama iki iş açmasın.
  const [starting, setStarting] = useState<string | null>(null);

  const appQuery = useQuery({
    queryKey: ["app", appId],
    queryFn: () => client.app(appId),
    enabled: Boolean(appId),
  });

  const jobsQuery = useQuery({
    queryKey: ["jobs", appId],
    queryFn: () => client.jobs({ app: appId, limit: 40 }),
    enabled: Boolean(appId),
    // Olay akışı kopsa da süren iş bitince sayfa kendini güncellesin.
    refetchInterval: (q) => (q.state.data?.some((j) => ACTIVE.has(j.status)) ? 4000 : false),
  });
  const jobs = jobsQuery.data;

  // job.update uygulama kimliği taşımıyor; hangi iş olursa olsun liste tazelenir (ucuz).
  useEventListener(
    (event) => {
      if (event.t === "job.update") {
        void queryClient.invalidateQueries({ queryKey: ["jobs", appId] });
      }
    },
    [appId],
  );

  if (!appQuery.data) {
    return (
      <>
        <PageHeader kicker="uygulama" title={appQuery.error ? "Uygulama açılamadı" : "…"} />
        <PageBody>
          <Card flush>
            {appQuery.error ? (
              <ErrorState error={appQuery.error} onRetry={() => void appQuery.refetch()} />
            ) : (
              <Empty>Yükleniyor…</Empty>
            )}
          </Card>
        </PageBody>
      </>
    );
  }

  const { app, jobs: available } = appQuery.data;
  const runnable = available.filter((j) => !j.id.startsWith("vps.") && !j.id.startsWith("bot."));
  const summaryOf = (id: string): JobSummary | undefined => available.find((j) => j.id === id);

  // Web/bot uygulamalarında mobil yayın hattı gürültü; yalnızca derlenip Play'e giden uygulamalarda.
  const showPipeline = Boolean(summaryOf("build.gradle") || summaryOf("play.upload"));
  const otherJobs = showPipeline ? runnable.filter((j) => !PIPELINE_SET.has(j.id)) : runnable;

  const latestOf = (type: string): JobRow | undefined => jobs?.find((j) => j.type === type);
  const versionCheck = latestOf("release.version.check");
  const findings = (versionCheck?.result as { findings?: { level: string; message: string }[] } | null)
    ?.findings;

  const current = currentVersions(jobs);
  const head = current ? headline(current.versions) : undefined;
  const pending = pendingBump(jobs);
  const history = bumpHistory(jobs);
  const lastBuild = latestSucceeded(jobs, "build.gradle")?.result as BuildResult | undefined;
  const lastUploadTrack = (latestOf("play.upload")?.params as { track?: string } | null)?.track;
  // Kontrol, son yükseltmeden ÖNCE çalıştıysa bulguları o anki dosyalara ait.
  const checkStale = Boolean(versionCheck) && current?.source.type === "release.version.bump";

  // Öneri yalnızca iş geçmişi okunduysa anlamlı; yoksa yanlış adımı öne çıkarır.
  const rec = jobs ? recommendNext(jobs) : undefined;
  const recJob = rec && rec !== "ota" && summaryOf(rec) ? rec : undefined;
  const otaSummary = summaryOf("ota.publish");

  // /apps/:id yeteneklerin anlık durumunu ayrı bir alanda vermiyor; iş listesindeki
  // `available` bayrağı zaten aynı bilgiyi taşıyor (yetenek başına tekilleştirilir).
  const capabilityState = new Map<string, boolean>();
  for (const j of available) {
    capabilityState.set(j.capability, (capabilityState.get(j.capability) ?? false) || j.available);
  }
  const ready = [...capabilityState.values()].filter(Boolean).length;
  const total = capabilityState.size;
  const tone: Tone = ready === 0 ? "idle" : ready === total ? "ok" : "warn";

  const runningIndex = running ? PIPELINE.findIndex((s) => s.job === running.type) : -1;
  const nextStep = runningIndex >= 0 ? PIPELINE[runningIndex + 1] : undefined;
  const nextSummary = nextStep ? summaryOf(nextStep.job) : undefined;

  const context = (fromPipeline = false): LaunchContext => ({
    current: head,
    pending,
    lastBuildPath: lastBuild?.path,
    lastTrack: lastUploadTrack,
    fromPipeline,
    runtime: {
      value: current?.versions.runtimeVersion,
      policy: current?.versions.runtimePolicy,
      pinned: app.ota?.runtimeVersionPinned,
    },
  });

  async function runNow(type: string): Promise<void> {
    if (starting) return;
    setStarting(type);
    try {
      const res = await client.createJob({ type, app: appId, params: { app: appId } });
      setRunning({ id: res.job.id, type });
      if (res.queuedOnly) {
        toast({ tone: "info", title: "Kuyruğa alındı", body: res.message ?? "Agent bağlanınca çalışacak." });
      }
      void queryClient.invalidateQueries({ queryKey: ["jobs", appId] });
    } catch (err) {
      toast({ tone: "danger", title: `${jobTitle(type)} başlatılamadı`, body: humanizeError(err) });
    } finally {
      setStarting(null);
    }
  }

  function open(summary: JobSummary | undefined, opts: Omit<Launching, "summary"> = {}): void {
    if (!summary) return;
    if (QUICK.has(summary.id) && opts.initialParams === undefined) void runNow(summary.id);
    else setLaunching({ summary, ...opts });
  }

  function retry(job: JobRow): void {
    const summary = summaryOf(job.type);
    if (!summary) {
      toast({ tone: "danger", title: "Tekrar denenemiyor", body: "Bu iş artık bu uygulama için tanımlı değil." });
      return;
    }
    // Hızlı işlerin tek parametresi uygulama; form açmak yalnızca bir tık ekler.
    if (QUICK.has(job.type)) void runNow(job.type);
    else setLaunching({ summary, initialParams: job.params, fromPipeline: PIPELINE_SET.has(job.type) });
  }

  return (
    <>
      <PageHeader
        kicker="uygulama"
        title={
          <span className="flex items-center gap-3">
            <AppIcon id={app.id} name={app.displayName} size={34} />
            {app.displayName}
          </span>
        }
        actions={
          app.capabilities.includes("store.assets") && (
            <Button icon={Store} onClick={() => navigate(`/apps/${appId}/store`)}>
              Mağaza
            </Button>
          )
        }
      />

      <PageBody>
        <div className="card flex flex-wrap items-center gap-x-4 gap-y-2 p-4">
          <span className="tag tag-outline">{app.platform}</span>
          <span className="mono min-w-0 text-2xs break-all text-neutral-500">{app.workspace}</span>
          {app.build?.backend && (
            <span className="text-2xs text-neutral-500">
              derleme: {app.build.script ? `npm run ${app.build.script.name}` : app.build.backend}
            </span>
          )}
          {app.ota && <span className="text-2xs text-neutral-500">kanal: {app.ota.channels.join(", ")}</span>}
          <div className="flex items-center gap-2 text-xs sm:ml-auto" style={{ color: toneColor[tone] }}>
            <Dot tone={tone} />
            <span>
              {ready}/{total} yetenek hazır
            </span>
          </div>
        </div>

        {showPipeline && (
          <Card title="Yayın hattı" hint="sürüm kontrolü geçmeden derleme başlatma" flush>
            {jobsQuery.error ? (
              <ErrorState error={jobsQuery.error} onRetry={() => void jobsQuery.refetch()} compact />
            ) : jobsQuery.isLoading ? (
              <Empty>İş geçmişi okunuyor…</Empty>
            ) : (
              <>
                {rec === "ota" && (
                  <div className="border-b border-neutral-900 px-4 py-3">
                    <Banner
                      tone="info"
                      kicker="OTA yeterli"
                      action={
                        otaSummary && (
                          <Button variant="primary" size="sm" onClick={() => open(otaSummary)}>
                            {jobTitle("ota.publish")}
                          </Button>
                        )
                      }
                    >
                      Değişiklik analizi native derleme gerektirmiyor; sürüm yükseltip derlemek bir
                      versionCode harcar.
                      {!otaSummary && " OTA bu uygulamada panel dışında, kendi betiğiyle yayınlanıyor."}
                    </Banner>
                  </div>
                )}
                <ol className="grid lg:grid-cols-5">
                  {PIPELINE.map((step, i) => (
                    <PipelineStep
                      key={step.job}
                      n={step.n}
                      title={step.title}
                      last={i === PIPELINE.length - 1}
                      job={latestOf(step.job)}
                      jobs={jobs}
                      summary={summaryOf(step.job)}
                      recommended={recJob === step.job}
                      starting={starting === step.job}
                      pendingNote={
                        step.job === "release.version.bump" && pending
                          ? `${fmtVersion(pending.version, pending.versionCode)} derlenmedi — sıradaki adım derleme`
                          : undefined
                      }
                      onShow={(job) => setRunning({ id: job.id, type: job.type })}
                      onStart={(summary) => open(summary, { fromPipeline: true })}
                    />
                  ))}
                </ol>
              </>
            )}
          </Card>
        )}

        {running && (
          <RunPanel
            key={running.id}
            jobId={running.id}
            type={running.type}
            next={
              nextStep && nextSummary
                ? { title: nextStep.title, onStart: () => open(nextSummary, { fromPipeline: true }) }
                : undefined
            }
            onRetry={retry}
            onClose={() => setRunning(null)}
          />
        )}

        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <Card
            title="Sürüm"
            hint={
              current
                ? `${current.source.type === "release.version.bump" ? "son yükseltmeden" : "son kontrolden"} · ${shortTime(current.source.createdAt)}`
                : "henüz okunmadı"
            }
            action={
              <>
                {summaryOf("release.version.check") && (
                  <Button
                    size="sm"
                    icon={RotateCw}
                    loading={starting === "release.version.check"}
                    onClick={() => void runNow("release.version.check")}
                  >
                    Yeniden oku
                  </Button>
                )}
                {summaryOf("release.version.bump") && (
                  <>
                    <Button size="sm" onClick={() => open(summaryOf("release.version.bump"))}>
                      Yükselt
                    </Button>
                    {/* Tüm değerler elle: sürüm adı, versionCode, runtime — geri alma dahil. */}
                    <Button
                      size="sm"
                      disabled={!head?.name}
                      title={head?.name ? undefined : "Önce sürüm dosyalarını oku"}
                      onClick={() =>
                        open(summaryOf("release.version.bump"), {
                          initialParams: {
                            app: appId,
                            semver: head?.name ?? "1.0.0",
                            versionCode: head?.code ?? 1,
                            bumpVersionCode: false,
                            commit: true,
                          },
                        })
                      }
                    >
                      Elle düzenle
                    </Button>
                  </>
                )}
              </>
            }
            flush
          >
            {jobsQuery.error ? (
              <ErrorState error={jobsQuery.error} onRetry={() => void jobsQuery.refetch()} compact />
            ) : jobsQuery.isLoading ? (
              <Empty>Yükleniyor…</Empty>
            ) : current && head ? (
              <>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 pt-4 pb-3">
                  <span className="mono text-xl leading-none text-neutral-50">{head.name ?? "?"}</span>
                  <span className="text-xs text-neutral-500">versionCode</span>
                  <span className="mono text-lg leading-none text-neutral-100">{head.code ?? "?"}</span>
                </div>
                <RuntimeStatus
                  value={current.versions.runtimeVersion}
                  policy={current.versions.runtimePolicy}
                  version={head.name}
                  pinned={app.ota?.runtimeVersionPinned}
                />
                {Object.entries(current.versions).map(([key, value]) => (
                  <div key={key} className="row items-baseline">
                    <span className="w-1/2 shrink-0 text-2xs text-neutral-500 sm:w-44">
                      {VERSION_LABELS[key] ?? key}
                    </span>
                    <span className="mono min-w-0 text-sm break-all text-neutral-100">
                      {value == null ? "—" : String(value)}
                    </span>
                  </div>
                ))}
              </>
            ) : (
              <Empty>"Yeniden oku" ile sürüm dosyalarını oku</Empty>
            )}

            {history.length > 0 && (
              <>
                <div className="kicker border-t border-neutral-900 px-4 pt-3 pb-1.5">Sürüm geçmişi</div>
                {history.slice(0, 8).map(({ job, result }) => (
                  <RowLink
                    key={job.id}
                    onClick={() => setRunning({ id: job.id, type: job.type })}
                    className="items-baseline text-xs"
                  >
                    <span className="mono text-neutral-500">
                      {result.from ? fmtVersion(result.from.version, result.from.versionCode) : "…"}
                    </span>
                    <span className="text-neutral-600" aria-hidden>
                      →
                    </span>
                    <span className="mono text-neutral-100">{fmtVersion(result.version, result.versionCode)}</span>
                    {result.from?.runtimeVersion !== undefined &&
                      result.versions?.runtimeVersion !== undefined &&
                      result.from.runtimeVersion !== result.versions.runtimeVersion && (
                        <span className="mono text-2xs text-warn">runtime → {result.versions.runtimeVersion}</span>
                      )}
                    <span className="mono ml-auto text-2xs text-neutral-600">{shortTime(job.createdAt)}</span>
                  </RowLink>
                ))}
              </>
            )}
          </Card>

          <Card
            title="Yayın öncesi okunacaklar"
            hint={
              checkStale
                ? "son kontrol, son yükseltmeden önce — güncellemek için Yeniden oku"
                : versionCheck
                  ? `son kontrol · ${shortTime(versionCheck.createdAt)}`
                  : undefined
            }
            flush
          >
            {findings?.length ? (
              findings.map((f, i) => (
                <div key={`${i}-${f.message}`} className="row items-start text-xs text-pretty text-neutral-300">
                  <span
                    className="shrink-0"
                    style={{
                      color:
                        f.level === "error"
                          ? "var(--color-danger)"
                          : f.level === "warn"
                            ? "var(--color-warn)"
                            : "var(--color-ok)",
                    }}
                  >
                    {f.level === "ok" ? "✓" : f.level === "warn" ? "⚠" : "✗"}
                  </span>
                  <span className="min-w-0">{f.message}</span>
                </div>
              ))
            ) : app.notes?.length ? (
              app.notes.map((note) => (
                <div key={note} className="row items-start text-xs text-pretty text-neutral-300">
                  <span className="shrink-0 text-neutral-700">—</span>
                  <span className="min-w-0">{note}</span>
                </div>
              ))
            ) : (
              <Empty>Not yok</Empty>
            )}
          </Card>
        </div>

        <Card
          id="app-jobs"
          title={showPipeline ? "Diğer işler" : "Çalıştırılabilir işler"}
          hint={showPipeline ? "yayın hattındakiler yukarıda" : undefined}
          flush
        >
          {otherJobs.length ? (
            otherJobs.map((job) => (
              <RowLink key={job.id} onClick={() => open(job)} className="flex-wrap gap-x-3 gap-y-1">
                <Dot tone={job.available ? "ok" : "idle"} />
                <span className="grid min-w-0 flex-1 gap-0.5">
                  <span className="text-sm text-neutral-100">{jobTitle(job.id)}</span>
                  <span className="mono truncate text-2xs text-neutral-600">{job.id}</span>
                </span>
                <span className="flex items-center gap-2 text-2xs text-neutral-500">
                  {job.available ? (job.estimate ?? "hazır") : "agent kapalı — kuyruğa alınır"}
                  {job.confirm === "totp" && <span className="tag tag-accent">kod ister</span>}
                </span>
              </RowLink>
            ))
          ) : (
            <Empty>Bu uygulamada çalıştırılabilir başka iş yok</Empty>
          )}
        </Card>

        <Card
          title="Bu uygulamanın son işleri"
          action={
            <Link to={`/jobs?app=${encodeURIComponent(appId)}`} className="btn btn-ghost btn-sm">
              Tümünü gör →
            </Link>
          }
          flush
        >
          {jobsQuery.error ? (
            <ErrorState error={jobsQuery.error} onRetry={() => void jobsQuery.refetch()} compact />
          ) : jobsQuery.isLoading ? (
            <Empty>Yükleniyor…</Empty>
          ) : jobs?.length ? (
            jobs.slice(0, 10).map((job) => {
              const line = stepSummary(job);
              return (
                <RowLink
                  key={job.id}
                  onClick={() => setRunning({ id: job.id, type: job.type })}
                  className="items-start"
                >
                  <span className="mt-[1px] shrink-0">
                    <StatusPill status={job.status} />
                  </span>
                  <span className="grid min-w-0 flex-1 gap-0.5">
                    <span className="truncate text-sm text-neutral-100">{jobTitle(job.type)}</span>
                    {line && <span className="mono truncate text-2xs text-neutral-500">{line}</span>}
                  </span>
                  <span className="mono shrink-0 text-2xs text-neutral-600">{shortTime(job.createdAt)}</span>
                </RowLink>
              );
            })
          ) : (
            <Empty>Henüz iş çalıştırılmadı</Empty>
          )}
        </Card>

        {launching && (
          <JobLauncher
            key={launching.summary.id}
            job={launching.summary}
            appId={appId}
            appName={app.displayName}
            channels={app.ota?.channels ?? ["production"]}
            buildScript={app.build?.script}
            context={context(launching.fromPipeline)}
            initialParams={launching.initialParams}
            onLaunched={(id) => {
              setRunning({ id, type: launching.summary.id });
              void queryClient.invalidateQueries({ queryKey: ["jobs", appId] });
            }}
            onClose={() => setLaunching(null)}
          />
        )}
      </PageBody>
    </>
  );
}

/**
 * Hattın bir adımı. Küçük ekranda dikey (bağlantı çizgisi solda), lg'de yatay
 * (çizgi dairelerin arasında). Durum satırı son ÇALIŞTIRMANIN sonucudur, güncel
 * bir engel değil; tıklanınca kaydını bu sayfada açar.
 */
function PipelineStep({
  n,
  title,
  last,
  job,
  jobs,
  summary,
  recommended,
  starting,
  pendingNote,
  onShow,
  onStart,
}: {
  n: number;
  title: string;
  last: boolean;
  job: JobRow | undefined;
  jobs: readonly JobRow[] | undefined;
  summary: JobSummary | undefined;
  recommended: boolean;
  starting: boolean;
  pendingNote?: string;
  onShow: (job: JobRow) => void;
  onStart: (summary: JobSummary) => void;
}): ReactNode {
  const status = job?.status;
  const active = status ? ACTIVE.has(status) : false;
  const tone: Tone = !status
    ? "idle"
    : status === "succeeded"
      ? "ok"
      : status === "failed"
        ? "danger"
        : active
          ? "accent"
          : "idle";
  const color = recommended && tone === "idle" ? toneColor.accent : toneColor[tone];

  let line = job ? stepSummary(job) : undefined;
  // Panel dışında geri alınan yükseltme eski "→ 1.2.0 · 5"i doğruymuş gibi göstermesin.
  if (line && job && bumpDrifted(job, jobs)) line += " · sonra değişti";

  return (
    <li
      className={`relative grid grid-cols-[24px_minmax(0,1fr)] content-start gap-x-3 gap-y-2 px-4 py-3.5 lg:grid-cols-1 ${
        recommended ? "bg-white/[0.025]" : ""
      } ${summary ? "" : "opacity-60"}`}
      aria-current={recommended ? "step" : undefined}
    >
      {!last && (
        <span
          aria-hidden
          className="absolute top-[42px] -bottom-[10px] left-[27.5px] w-px lg:top-[26px] lg:right-[-8px] lg:bottom-auto lg:left-[48px] lg:h-px lg:w-auto"
          style={{
            background:
              tone === "ok"
                ? "color-mix(in srgb, var(--color-ok) 45%, transparent)"
                : "var(--color-neutral-800)",
          }}
        />
      )}
      <span
        className="row-span-4 grid size-6 place-items-center rounded-full border text-2xs font-medium lg:row-span-1"
        style={{
          borderColor: color,
          color,
          background: tone === "ok" || tone === "danger" ? `color-mix(in srgb, ${color} 12%, transparent)` : undefined,
        }}
      >
        {status === "succeeded" ? (
          <Check size={13} strokeWidth={2.4} aria-label="başarılı" />
        ) : status === "failed" ? (
          <X size={13} strokeWidth={2.4} aria-label="başarısız" />
        ) : active ? (
          <Loader2 size={13} className="spin" aria-label="çalışıyor" />
        ) : (
          n
        )}
      </span>

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-neutral-100">{title}</span>
        {recommended && <span className="tag tag-accent">sıradaki</span>}
      </div>

      {job ? (
        <button
          type="button"
          onClick={() => onShow(job)}
          className="grid cursor-pointer justify-items-start gap-0.5 text-left text-2xs underline-offset-2 hover:underline"
          title="Son çalıştırmanın kaydını aç"
        >
          <span style={{ color: toneColor[tone] }}>
            {statusLabel(job.status)} · {shortTime(job.createdAt)}
          </span>
          {line && <span className="mono break-words text-neutral-300">{line}</span>}
        </button>
      ) : (
        <div className="text-2xs text-neutral-600">hiç çalışmadı</div>
      )}

      {pendingNote && <div className="text-2xs text-warn">{pendingNote}</div>}

      {summary ? (
        <Button
          variant={recommended ? "primary" : "secondary"}
          size={recommended ? "md" : "sm"}
          icon={Play}
          loading={starting}
          className="justify-self-start"
          onClick={() => onStart(summary)}
        >
          {job ? (recommended ? "Başlat" : "Tekrar çalıştır") : "Başlat"}
        </Button>
      ) : (
        <div className="text-2xs text-neutral-600">bu uygulamada yok</div>
      )}
    </li>
  );
}

/**
 * runtimeVersion'ın durumu tek bakışta: sabit mi, politikaya mı bağlı, registry'de
 * sabitlenmiş değerle uyuşuyor mu. OTA yalnızca aynı runtime'daki kurulumlara
 * gittiği için yanlış runtime, güncellemenin sessizce kimseye ulaşmaması demek.
 */
function RuntimeStatus({
  value,
  policy,
  version,
  pinned,
}: {
  value?: string;
  policy?: string;
  version?: string;
  pinned?: string;
}): ReactNode {
  let tone: "ok" | "warn" | "danger" | "idle" = "idle";
  let title: string;
  let detail: string;
  if (policy) {
    tone = "warn";
    title = `Politika: ${policy}`;
    detail = "Runtime sabit değil; sürüm değişince kendiliğinden değişir. Her sürüm yükseltmesi yeni bir Play derlemesi gerektirir.";
  } else if (!value) {
    title = "Tanımsız";
    detail = "app.json'da runtimeVersion yok.";
  } else if (pinned && pinned !== value) {
    tone = "danger";
    title = `Sabit: ${value} — registry ${pinned} bekliyor`;
    detail = "Registry'deki sabit değerle uyuşmuyor; sürüm kontrolü bunu engel sayar.";
  } else if (pinned) {
    tone = "ok";
    title = `Sabit: ${value} · registry'de sabitlenmiş`;
    detail = "Değişmesi beklenmiyor; OTA'lar bu runtime'daki tüm kurulumlara gider.";
  } else {
    tone = value === version ? "ok" : "warn";
    title = `Sabit: ${value}`;
    detail =
      value === version
        ? "Sürüm adıyla aynı. Registry'de sabitlenmemiş."
        : `Sürümden (${version ?? "?"}) bağımsız sabit değer. Bilinçliyse registry'de sabitlemek iyi olur.`;
  }
  const color =
    tone === "ok" ? "var(--color-ok)" : tone === "warn" ? "var(--color-warn)" : tone === "danger" ? "var(--color-danger)" : "var(--color-neutral-500)";
  return (
    <div className="mx-4 mb-3 grid gap-0.5 rounded-[var(--radius-sm)] border px-3 py-2"
      style={{ borderColor: `color-mix(in srgb, ${color} 35%, transparent)`, background: `color-mix(in srgb, ${color} 6%, transparent)` }}
    >
      <span className="text-2xs tracking-[0.06em] text-neutral-500 uppercase">runtime</span>
      <span className="mono text-sm" style={{ color }}>{title}</span>
      <span className="text-2xs text-neutral-500">{detail}</span>
    </div>
  );
}
